import { getApp, getApps, initializeApp } from "firebase/app";
import type { AppCheck } from "firebase/app-check";
import type { Analytics } from "firebase/analytics";
import { clearGoogleAnalyticsCookies, hasAnalyticsConsent } from "./cookie-consent";

const firebaseConfig = {
  apiKey: "AIzaSyChbSwIoG1f09A1dy1Pr5ijAV3WqIGNCwM",
  authDomain: "socrattica.firebaseapp.com",
  projectId: "socrattica",
  storageBucket: "socrattica.firebasestorage.app",
  messagingSenderId: "896166829181",
  appId: "1:896166829181:web:e3ecc850f7f82afc316514",
  measurementId: "G-DEMH12MTTE",
};

export const firebaseApp = getApps().length === 0 ? initializeApp(firebaseConfig) : getApp();

const RECAPTCHA_ENTERPRISE_SITE_KEY = "6LdFS20tAAAAABxBhl5-jKSEl_QcX62x61eaPQFv";
let appCheck: AppCheck | null = null;
let appCheckPromise: Promise<AppCheck | null> | undefined;

async function initializeFirebaseAppCheck() {
  if (typeof window === "undefined") return null;
  if (appCheck) return appCheck;
  if (appCheckPromise) return appCheckPromise;

  appCheckPromise = import("firebase/app-check").then(
    ({ initializeAppCheck, ReCaptchaEnterpriseProvider }) => {
      const debugToken = import.meta.env.VITE_FIREBASE_APPCHECK_DEBUG_TOKEN;
      if (import.meta.env.DEV && debugToken) {
        (
          self as typeof self & {
            FIREBASE_APPCHECK_DEBUG_TOKEN?: string;
          }
        ).FIREBASE_APPCHECK_DEBUG_TOKEN = debugToken;
      }

      appCheck = initializeAppCheck(firebaseApp, {
        provider: new ReCaptchaEnterpriseProvider(RECAPTCHA_ENTERPRISE_SITE_KEY),
        isTokenAutoRefreshEnabled: true,
      });
      return appCheck;
    },
  );
  return appCheckPromise;
}

export async function appCheckFetch(input: RequestInfo | URL, init: RequestInit = {}) {
  const instance = await initializeFirebaseAppCheck();
  if (!instance) return fetch(input, init);

  let token: string;
  try {
    const { getToken: getAppCheckToken } = await import("firebase/app-check");
    token = (await getAppCheckToken(instance, false)).token;
  } catch {
    throw new Error(
      "No pudimos verificar la seguridad de esta sesión. Recarga la página e inténtalo nuevamente.",
    );
  }

  const headers = new Headers(init.headers);
  headers.set("X-Firebase-AppCheck", token);
  return fetch(input, { ...init, headers });
}

let analyticsPromise: Promise<Analytics | null> | undefined;

export function initializeFirebaseAnalytics() {
  if (typeof window === "undefined" || !hasAnalyticsConsent()) {
    return Promise.resolve(null);
  }

  analyticsPromise ??= import("firebase/analytics")
    .then(async ({ initializeAnalytics, isSupported, setAnalyticsCollectionEnabled }) => {
      if (!(await isSupported())) return null;
      const analytics = initializeAnalytics(firebaseApp, {
        config: { send_page_view: false },
      });
      setAnalyticsCollectionEnabled(analytics, true);
      return analytics;
    })
    .catch((error) => {
      console.warn("Firebase Analytics no pudo inicializarse.", error);
      return null;
    });

  return analyticsPromise;
}

export async function updateFirebaseAnalyticsConsent(enabled: boolean) {
  if (typeof window === "undefined") return;

  if (enabled) {
    const analytics = await initializeFirebaseAnalytics();
    if (!analytics) return;
    const { setAnalyticsCollectionEnabled, setConsent } = await import("firebase/analytics");
    setAnalyticsCollectionEnabled(analytics, true);
    setConsent({ analytics_storage: "granted" });
    return;
  }

  if (analyticsPromise) {
    const analytics = await analyticsPromise;
    if (analytics) {
      const { setAnalyticsCollectionEnabled, setConsent } = await import("firebase/analytics");
      setConsent({ analytics_storage: "denied" });
      setAnalyticsCollectionEnabled(analytics, false);
    }
  }

  clearGoogleAnalyticsCookies();
}
